import { type Href } from 'expo-router';
import { View } from 'react-native';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { cn } from '@/components/ui/cn';
import { Surface } from '@/components/ui/Surface';
import { Text } from '@/components/ui/Text';

import { UsageMeter } from './UsageMeter';

export interface UpsellCardProps {
  checklistsUsed: number;
  checklistsLimit: number;
  runsUsed: number;
  runsLimit: number;
  headline?: string;
  sub?: string;
  /** Navigation target for "Go Pro" — renders a real `<a href>` on web. */
  href?: Href;
  onGoPro?: () => void;
  className?: string;
}

/** The quiet ink promo card under the Home library on the free plan. */
export function UpsellCard({
  checklistsUsed,
  checklistsLimit,
  runsUsed,
  runsLimit,
  headline = 'Room for one more template',
  sub = 'Free includes 2 templates and 10 runs. Go Pro for unlimited templates, runs, and live sharing.',
  href,
  onGoPro,
  className,
}: UpsellCardProps) {
  return (
    <Surface variant="ink" className={cn('gap-3 p-4', className)}>
      <View className="flex-row items-center justify-between">
        <Text
          variant="none"
          className="font-accent text-xs uppercase tracking-[0.12em] text-faint"
        >
          Free plan
        </Text>
        <Badge label="Pro" tone="signal" />
      </View>
      <Text variant="heading" className="text-on-ink">
        {headline}
      </Text>
      <Text variant="caption" className="text-faint">
        {sub}
      </Text>
      <View className="mt-1 gap-2.5">
        <UsageMeter
          tone="ink"
          label="Templates"
          used={checklistsUsed}
          limit={checklistsLimit}
        />
        <UsageMeter tone="ink" label="Runs" used={runsUsed} limit={runsLimit} />
      </View>
      <Button
        className="mt-1"
        variant="primary"
        block
        label="Go Pro"
        href={href}
        onPress={onGoPro}
      />
    </Surface>
  );
}
